import Tooltip from "components/atoms/Tooltip";
import { Paragraph } from "components/atoms/Typography";
import { displayValueWithUnit, getDisplayInfo } from "utils/formatting";
import { IncreaseDecreasePercentage } from "./CardFunctions";
import { DataSection, StatisticInfo } from "./CardTypes";

interface CardBodyProps {
    sections: DataSection[];
    invertedColor?: boolean;
    multipleInvertedColors?: boolean;
}

function CardBody({ sections, invertedColor, multipleInvertedColors }: CardBodyProps) {
    return (
        <>
            {sections.map((section: DataSection, index: number) => (
                <div
                    className="card__body"
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${index}${section[0].displayInfoKey}`}
                >
                    <CardBodySection
                        section={section}
                        sectionIndex={index}
                        invertedColor={invertedColor}
                        multipleInvertedColors={multipleInvertedColors}
                    />
                </div>
            ))}
        </>
    );
}

interface CardBodySectionProps {
    section: DataSection;
    sectionIndex: number;
    invertedColor?: boolean;
    multipleInvertedColors?: boolean;
}

function CardBodySection({ section, sectionIndex, invertedColor, multipleInvertedColors }: CardBodySectionProps) {
    return (
        <>
            {addDividerToCardSection(sectionIndex) && <div className="card__divider" />}
            {section.map((info: StatisticInfo) => (
                <CardRow
                    info={info}
                    key={`${info.displayInfoKey}${sectionIndex}${info.title}`}
                    invertedColor={multipleInvertedColors ? info.invertedColor : invertedColor}
                />
            ))}
        </>
    );
}

const addDividerToCardSection = (sectionIndex: number): boolean => (sectionIndex + 1) % 2 === 0;

interface CardRowProps {
    info: StatisticInfo;
    invertedColor?: boolean;
}

function CardRow({ info, invertedColor }: CardRowProps) {
    const {
        value,
        percentage,
        tooltipText,
        tooltipElement,
        displayInfoKey,
        title,
        increaseDecreasePercentage,
        valueColor,
        titleIcon,
        bold,
    } = info;

    const { humanReadable, unit, formatFunction } =
        typeof value === "number" ? getDisplayInfo(displayInfoKey, value) : getDisplayInfo(displayInfoKey);

    return (
        <div className="card__row">
            <div className="card__title-tooltip">
                <Paragraph size="small" style={bold ? { fontWeight: "bold" } : {}}>
                    {titleIcon && titleIcon}
                    {title || humanReadable}
                </Paragraph>
                {tooltipText && <Tooltip tooltipBody={tooltipText} />}
            </div>
            <div className="card__value-and-percentage">
                <Paragraph
                    size="small"
                    style={{
                        color: valueColor,
                        ...(bold ? { fontWeight: "bold" } : {}),
                    }}
                >
                    {displayValueWithUnit(value, unit, (v) => formatFunction(v))}
                    {percentage !== undefined && <span className="percentage"> {percentage}%</span>}
                </Paragraph>
                {increaseDecreasePercentage !== undefined && (
                    <IncreaseDecreasePercentage value={increaseDecreasePercentage} invertedColor={invertedColor} />
                )}
                {tooltipElement && <Tooltip tooltipBody={tooltipElement} />}
            </div>
        </div>
    );
}

export default CardBody;
